<script setup>
import { computed } from 'vue'

import logger from "~/shared/utils/logger";

const props = defineProps({
  searchPath: {
    type: String,
    default: null
  },
  searchGuide: {
    type: String,
    default: null
  },
  currentOrder: {
    type: String,
    default: null
  },
})

const route = useRoute();
logger.log("ChapterNav route path:", route.path, "searchPath prop:", props.searchPath, "searchGuide prop:", props.searchGuide, "currentOrder prop:", props.currentOrder);

let guide = {
  content_type: ContentTypeEnum.Guide,
  chapters: []
}

let currentChapter = null;
let currentChapterIndex = null;
let firstChapter = null;
let lastChapter = null;
let previousChapterIndex = null;
let nextChapterIndex = null;
let previousChapter = null;
let nextChapter = null;

const emojiBankButtons = computed(() => {
  const buttons = []

  if (firstChapter) {
    buttons.push({
      to: firstChapter.path,
      emoji: '⏮️',
      title: `Start over: ${firstChapter.tagline || firstChapter.title}`,
    })
  }

  // if (previousChapter) {
  //   buttons.push({
  //     to: previousChapter.path,
  //     emoji: '◀️',
  //     title: `Previous: ${previousChapter.tagline || previousChapter.title}`,
  //   })
  // }

  buttons.push({
    to: guide?.path || props.searchPath,
    emoji: ':arrow_up_small:',
    title: `Back to guide: ${guide?.title || ''}`,
  })

  // if (nextChapter) {
  //   buttons.push({
  //     to: nextChapter.path,
  //     emoji: '▶️',
  //     title: `Next: ${nextChapter.tagline || nextChapter.title}`,
  //   })
  // }

  if (lastChapter) {
    buttons.push({
      to: lastChapter.path,
      emoji: '⏭️',
      title: `Last: ${lastChapter.tagline || lastChapter.title}`,
    })
  }

  return buttons
})

const emojiBankTo = computed(() => emojiBankButtons.value.map((button) => button.to))
const emojiBankEmoji = computed(() => emojiBankButtons.value.map((button) => button.emoji))
const emojiBankTitle = computed(() => emojiBankButtons.value.map((button) => button.title))

if (props.searchGuide && props.searchGuide.length > 0 && props.searchPath && props.searchPath.length > 0) {

  const queryName = props.searchPath.slice(1).replace(/\//g, "_") + `-chapter_guide_${props.searchGuide}`;

  logger.log(`Guide:query props name: ${queryName}, searchPath: ${props.searchPath}, searchGuide: ${props.searchGuide}`);


  const { data: guideChapters } = await useAsyncData(queryName, () => {
    const query = queryCollection('content')
      .where('path', 'LIKE', `${props.searchPath}%`)
      .where('guide', 'LIKE', `%${props.searchGuide}%`)

    if(import.meta.env.PROD) {
      query.where('published', '==', PublishedStatusEnum.Published)
    }

    return query.all()
  });

  const guides = guideChapters.value.filter(guide =>
    guide.content_type == "guide"
  )

  const foundGuide = guides.length > 0 ? guides[0] : null;

  if(guides.length > 1) {
    logger.warn(`Multiple guides found. This may indicate an issue with your content collection data. Check your content collection and ensure there is only one document with content_type: guide, guide: ${props.searchGuide}, and a path starting with ${props.searchPath}.`);

    for (const item of guides) {
      logger.warn(`guide: ${item.guide} ${item.content_type} chapter: ${item.title}, path: ${item.path}`);
    }
    logger.warn("ChapterNav", "More than one guide found");
    logger.log("ChapterNav", `first guide found: ${foundGuide.title}, path: ${foundGuide.path}`);
  }

  foundGuide.chapters = guideChapters.value.filter(chapter =>
    chapter.content_type != ContentTypeEnum.Guide &&
    chapter.peer_order > 0
  ).sort((a, b) => (a.peer_order || 0) - (b.peer_order || 0)); // sort chapters by peer_order
  //

  logger.log("ChapterNav", `query result filtered: chapters: ${foundGuide.chapters.length}`);

  guide = foundGuide ? { ...foundGuide} : guide;

  currentChapter = guide.chapters.find(chapter => chapter.peer_order == props.currentOrder);
  currentChapterIndex = guide.chapters.findIndex(chapter => chapter.peer_order == props.currentOrder);
  firstChapter = guide.chapters[0] || null;
  lastChapter = guide.chapters[guide.chapters.length - 1] || null;
  previousChapterIndex = currentChapterIndex > 0 ? currentChapterIndex - 1 : undefined;
  nextChapterIndex = currentChapterIndex >= 0 && currentChapterIndex < guide.chapters.length - 1 ? currentChapterIndex + 1 : undefined;

  previousChapter = previousChapterIndex !== undefined ? guide.chapters[previousChapterIndex] : null;
  nextChapter = nextChapterIndex !== undefined ? guide.chapters[nextChapterIndex] : null;

  if(currentChapter) {
    logger.log("ChapterNav", `current chapter: ${currentChapter.title}, path: ${currentChapter.path}`);
    if(previousChapter) {
      logger.log("ChapterNav", `previous chapter: ${previousChapter.title}, path: ${previousChapter.path}`);
    } else {
      logger.log("ChapterNav", "No previous chapter found. This may be the first chapter in the guide.");
    }
    if(nextChapter) {
      logger.log("ChapterNav", `next chapter: ${nextChapter.title}, path: ${nextChapter.path}`);
    } else {
      logger.log("ChapterNav", "No next chapter found. This may be the last chapter in the guide.");
    }
  } else {
    logger.warn("ChapterNav", `No current chapter found for order: ${props.currentOrder}. Check that the currentOrder prop is correct and matches the peer_order of one of the chapters in the guide.`);
  }
} else {
  logger.warn("ChapterNav", "No valid foundData or searchGuide prop provided. Rendering empty guide with no chapters.")
}

</script>

<template>
  <div v-if="guide && guide.chapters && guide.chapters.length > 0">
    <div
      id="guide-title"
      class="guide-title"
    >{{ guide.title }}</div>
    <div class="chapter-tile-bar not-prose">
      <TileButton
        v-if="previousChapter"
        :key="previousChapter.path"
        :to="previousChapter.path"
        :image="previousChapter.image"
        :full="previousChapter.title"
        :short="previousChapter.tagline"
        :index="previousChapterIndex"
        bg-emoji="◀️"
      />
      <TileButton
        v-if="nextChapter"
        :key="nextChapter.path"
        :to="nextChapter.path"
        :image="nextChapter.image"
        :full="nextChapter.title"
        :short="nextChapter.tagline"
        :index="nextChapterIndex"
        bg-emoji="▶️"
      />
    </div>
        <div class="chapter-nav-bar not-prose">
      <EmojiBank
        :to="emojiBankTo"
        :emoji="emojiBankEmoji"
        :title="emojiBankTitle"
      />
    </div>
  </div>
  <div v-else>
    <p>No guide found.</p>
  </div>
</template>

<style lang="css" scoped>
.guide-title {
  @apply font-semibold mb-2
}

.chapter-nav-bar {
  @apply flex items-center justify-center mb-2
}

.chapter-tile-bar {
  @apply grid gap-2 grid-cols-1 sm:grid-cols-2 mb-2
}

</style>